import { addOpacityToHexColor } from "@src/lib/utils/color";
import React, { FC, IframeHTMLAttributes, useMemo } from "react";
import Text from "@src/lib/ui/Text";
import Spacer from "@src/lib/ui/Spacer";
import useBrandColor from "@src/features/settings/hooks/useBrandColor";
import OpenLinkPill from "./OpenLinkPill";
import { cn } from "@src/lib/utils/cn";
import LinkIcon from "@src/lib/icons/18px/link";

type Props = {
  url: string;
  loading: boolean;
  ogData: OgData | undefined;
  ogVideoIframeProps?: Partial<IframeHTMLAttributes<HTMLIFrameElement>>;
  ogImageProps?: Partial<React.ImgHTMLAttributes<HTMLImageElement>>;
};

const OpenGraphLinkPreview: FC<Props> = ({
  url,
  ogData,
  ogVideoIframeProps,
  ogImageProps,
}) => {
  const brandColor = useBrandColor();
  const centerContent = useMemo(() => {
    const ogTitle = ogData?.["og:title"];
    if (ogData?.["og:video:url"]) {
      const url = ogData?.["og:video:url"];
      return (
        <iframe
          id={"video-iframe"}
          className="cursor-pointer rounded-xl border-none outline-none"
          title={ogTitle}
          src={url}
          {...ogVideoIframeProps}
        />
      );
    } else if (ogData?.["og:image"]) {
      let img = ogData?.["og:image"];
      if (img.startsWith("/")) {
        img = ogData?.["og:url"] + img;
      }
      return (
        <img
          title={ogTitle}
          src={img}
          alt={ogData?.["og:description"] || ogTitle}
          {...ogImageProps}
          style={{
            maxWidth: "100%",
            maxHeight: 200,
            objectFit: "cover",
            ...ogImageProps?.style,
          }}
        />
      );
    } else {
      return (
        <div
          className="flex flex-1 items-center justify-center"
          style={{
            width: 300,
            aspectRatio: "327/179",
            backgroundColor: addOpacityToHexColor(brandColor, 0.2),
          }}
        >
          <LinkIcon className="size-[100px]" style={{ color: brandColor }} />
        </div>
      );
    }
  }, [brandColor, ogData, ogImageProps, ogVideoIframeProps]);
  return (
    <div
      id={"PromptMediaFileAndLinkContainer"}
      className="flex flex-1 flex-col items-center self-stretch bg-gray-alpha-5 p-[18px]"
    >
      <div className="flex-1" />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex cursor-pointer flex-col overflow-hidden rounded-[20px] shadow-[0px_0px_3.58904px_rgba(0,0,0,0.14),0px_7.17809px_54.36px_rgba(0,0,0,0.11)]"
      >
        <div className="bg-white p-2">
          <div className="overflow-hidden rounded-2xl bg-gray-alpha-4">
            {centerContent}
          </div>
        </div>
        <div
          className={cn(
            "flex flex-col items-center px-4 pb-2 self-stretch bg-white",
            ogData?.["og:title"] ? "h-[59px]" : "h-[42px]",
          )}
        >
          <Text
            type={"P1"}
            fontWeight={"SemiBold"}
            ellipsis={true}
            className="self-stretch"
          >
            {ogData?.["og:title"] || url}
          </Text>
          {ogData?.["og:title"] && (
            <>
              <Spacer size={2} />
              <Text
                type={"P2"}
                fontWeight={"SemiBold"}
                ellipsis={true}
                className={cn("self-stretch", "text-gray-7")}
              >
                {url}
              </Text>
            </>
          )}
        </div>
      </a>
      <div className="flex flex-1 flex-col">
        <Spacer size={24} />
        <OpenLinkPill url={url} text={"View Link"} />
      </div>
    </div>
  );
};

export default OpenGraphLinkPreview;

export type OgData = {
  "og:title": string | undefined;
  "og:description": string | undefined;
  "og:image": string | undefined;
  "og:url": string | undefined;
  "og:video:url": string | undefined;
  "og:image:height": string | undefined;
  "og:image:width": string | undefined;
  "og:site_name": string | undefined;
  "og:type": string | undefined;
  "og:video:height": string | undefined;
  "og:video:secure_url": string | undefined;
  "og:video:tag": string | undefined;
  "og:video:type": string | undefined;
  "og:video:width": string | undefined;
};
